import { makeAutoObservable, runInAction } from "mobx";
import { RootStore } from "@intrasync/stores";

export default class ScheduleGeneratorStore {
    // Observables
    isLoading = false;
    error: string | null = null;
    currentJob: any = null;
    scheduleData: any = null;
    optimizationParameters: any = {};
    constraints: any = {};
    generatedSchedule: any = null;

    // Constructor
    constructor(private rootStore: RootStore) {
        makeAutoObservable(this);
    }

    // Actions
    setLoading(loading: boolean) {
        this.isLoading = loading;
    }

    setError(error: string | null) {
        this.error = error;
    }

    // Async actions
    async initializeGenerator(jobId?: string) {
        try {
            this.setLoading(true);
            this.setError(null);

            // Mock data for now
            const mockJob = jobId ? {
                id: jobId,
                jobNumber: "JOB-2024-001",
                projectName: "Office Building A",
                client: "ABC Construction",
                totalPanels: 45,
            } : null;

            runInAction(() => {
                this.currentJob = mockJob;
                this.optimizationParameters = {
                    priority: "efficiency",
                    maxduration: 30,
                    resourceConstraints: true,
                };
                this.constraints = {
                    startDate: new Date().toISOString().split('T')[0],
                    endDate: null,
                    availableResources: 10,
                };
            });
        } catch (error: any) {
            runInAction(() => {
                this.setError(error.message || "Failed to initialize generator");
            });
            console.error("Failed to initialize generator:", error);
        } finally {
            this.setLoading(false);
        }
    }

    async generateSchedule() {
        try {
            this.setLoading(true);
            this.setError(null);

            // Mock schedule generation
            const mockSchedule = {
                id: `schedule-${Date.now()}`,
                jobId: this.currentJob?.id,
                generatedAt: new Date().toISOString(),
                estimatedduration: 25,
                tasks: [
                    {
                        id: "task-1",
                        name: "Panel Design Phase",
                        startDate: "2024-07-10",
                        endDate: "2024-07-20",
                        duration: 10,
                        resources: ["Designer A", "Designer B"],
                    },
                    {
                        id: "task-2",
                        name: "Production Phase",
                        startDate: "2024-07-21",
                        endDate: "2024-08-05",
                        duration: 15,
                        resources: ["Production Line 1", "Production Line 2"],
                    },
                ],
            };

            // Simulate API call delay
            await new Promise(resolve => setTimeout(resolve, 2000));

            runInAction(() => {
                this.generatedSchedule = mockSchedule;
            });
        } catch (error: any) {
            runInAction(() => {
                this.setError(error.message || "Failed to generate schedule");
            });
            console.error("Failed to generate schedule:", error);
        } finally {
            this.setLoading(false);
        }
    }

    async approveSchedule() {
        try {
            this.setLoading(true);
            this.setError(null);

            // Mock approval process
            console.log("Approving schedule:", this.generatedSchedule);

            // Simulate API call delay
            await new Promise(resolve => setTimeout(resolve, 1000));

            runInAction(() => {
                if (this.generatedSchedule) {
                    this.generatedSchedule.status = "approved";
                    this.generatedSchedule.approvedAt = new Date().toISOString();
                }
            });
        } catch (error: any) {
            runInAction(() => {
                this.setError(error.message || "Failed to approve schedule");
            });
            console.error("Failed to approve schedule:", error);
        } finally {
            this.setLoading(false);
        }
    }

    // Computed values
    get hasGeneratedSchedule() {
        return this.generatedSchedule !== null;
    }

    get isScheduleApproved() {
        return this.generatedSchedule?.status === "approved";
    }
}
